import {NetworkTypesEnum, TypeLocalNameEnum} from 'domain/usecases';
import {FeaturesEnum, OperationStateTypesEnum, QueryParamsEnum} from 'presentation/constants';
import {useHostName} from 'presentation/hooks';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useReverseNetworkOrder} from 'presentation/hooks/useReverseNetworkOrder';
import {MIN_DISTANCE_IN_KM_HEALTH} from 'presentation/modules/booking/health/ui/components/HealthSearchBar/hooks/useHealthSearchBar/constants';
import {FlowEnum} from 'presentation/modules/booking/shared/constants/flowEnum';
import {TelemedicineEnum} from 'presentation/modules/booking/shared/constants/telemedicineEnum';
import {
	KM_TO_REFETCH,
	MANUAL_DISTANCE_ORIGIN,
	MAX_DISTANCE_FILTER_UNITS,
} from 'presentation/modules/booking/shared/constants/unitsInfo';
import {useFilteredNetworkList} from 'presentation/modules/booking/shared/hooks/useFilteredNetworkList';
import {useRequestSchedulePermission} from 'presentation/modules/booking/shared/hooks/useRequestSchedulePermission';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useHideSearchBarStore} from 'presentation/modules/booking/shared/store/useHideSearchBarStore';
import {useUnitsFetchStateStore} from 'presentation/modules/booking/shared/store/useUnitsFetchStateStore';
import {useUnitsListStore} from 'presentation/modules/booking/shared/store/useUnitsListStore';
import {Unit} from 'presentation/modules/booking/shared/store/useUnitsListStore/types';
import {isNotAllowedForSpecialtyError} from 'presentation/modules/booking/shared/utils/isNotAllowedForSpecialtyError';
import {useQueryParams} from 'presentation/pages';
import {isUnprocessableError} from 'presentation/utils/isUnprocessableError';
import {useEffect, useRef, useState} from 'react';
import {useGetHealthProviderAccreditedMutation} from '../../mutations/useGetHealthProviderAccreditedMutation';
import {useGetHealthProviderPreviewMutation} from '../../mutations/useGetHealthProviderPreviewMutation';
import {useGetHealthUnitsMutation} from '../../mutations/useGetHealthUnitsMutation';
import {getNetworkVisibility} from '../../utils/getNetworkVisibility';
import {hasOnlyTelemedicineUnit} from '../../utils/hasOnlyTelemedicineUnit';
import {useHealthNetworkVisibility} from '../useHealthNetworkVisibility';

export function useHealthUnits() {
	const {isGndiSaoPaulo} = useHostName();
	const bookingData = useBookingStore(state => state.bookingData);
	const unitResponseState = useUnitsListStore(state => state.unitsList) as Unit<FlowEnum.HEALTH>;
	const setUnitResponseState = useUnitsListStore(state => state.setUnitsList);
	const setHideSearchBarState = useHideSearchBarStore(state => state.setHideSearchBar);
	const [hasUnitsUpToMaxDistanceState, setHasUnitsUpToMaxDistanceState] = useState(true);
	const operationState = useUnitsFetchStateStore(state => state.unitListFetchState);
	const setOperationState = useUnitsFetchStateStore(state => state.setUnitListFetchState);
	const [specialtyNotAvailableOnlineState, setSpecialtyNotAvailableOnlineState] = useState(false);
	const [notFoundErrorState, setNotFoundErrorState] = useState(false);
	const [isPreviewUnitsFetchedState, setIsPreviewUnitsFetchedState] = useState(false);
	const {showAccreditedNetworkToHap} = useHealthNetworkVisibility();
	const {isEnabled: isUnitsNetworksEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_BOOKING_UNITS_NETWORKS,
	);
	const {isEnabled: pacmanFlagIsEnabled} = useFeatureFlag(FeaturesEnum.PORTAL_FEATURE_PACMAN);
	const {
		SEARCH_PARAMS: {query, localityDistance, distanceOrigin},
		setSearchParams,
	} = useQueryParams();

	const {cityName, isManualLocation, ufCode, specialtyCode, typeLocalName} = bookingData;
	const filteredNetworkList = useFilteredNetworkList(query) as Unit<FlowEnum.HEALTH>;

	const isUsingGeolocation = !isManualLocation;
	const isManualDistanceSearch = distanceOrigin === MANUAL_DISTANCE_ORIGIN;
	const effectiveLocalityDistance =
		localityDistance || (isUsingGeolocation ? MIN_DISTANCE_IN_KM_HEALTH : '');
	const {shouldReverseNetworkOrder} = useReverseNetworkOrder({flowType: 'HEALTH'});

	const {
		handleGetHealthRequestSchedulePermission,
		healthRequestSchedulePermissionState,
		resetHealthRequestScheduleState,
	} = useRequestSchedulePermission();

	const abortControllerRef = useRef<AbortController | null>(null);

	const {mutateAsync: getHealthProviderAccredited} = useGetHealthProviderAccreditedMutation();
	const {mutateAsync: getHealthUnits} = useGetHealthUnitsMutation();
	const {mutateAsync: getHealthProviderPreview} = useGetHealthProviderPreviewMutation();

	const requestSchedulePermissionParams = {
		city: cityName?.toUpperCase(),
		stateCode: ufCode?.toUpperCase(),
		specialtyCode,
		typeLocalName: isManualLocation
			? typeLocalName || TypeLocalNameEnum.CIDADE
			: TypeLocalNameEnum.CIDADE,
	};

	const unitsList = filteredNetworkList;

	const allowsRequestScheduleRegistration = Boolean(
		healthRequestSchedulePermissionState?.allowsRegistration && pacmanFlagIsEnabled,
	);

	const isSelfNetwork = unitResponseState?.networkType?.name === NetworkTypesEnum.SELF;

	const showLoadingState =
		operationState === OperationStateTypesEnum.SUBMITTING ||
		operationState === OperationStateTypesEnum.PENDING;

	const showErrorState = operationState === OperationStateTypesEnum.ERROR;

	const {unitsByDistance, unitsByLocality} = unitResponseState?.units ?? {};

	const notFoundUnits =
		(unitsByLocality?.length === 0 && unitsByDistance?.length === 0) ||
		notFoundErrorState ||
		!isSelfNetwork;
	const notFoundAccreditedUnits = unitResponseState?.units?.unitsSupportAccredited?.length === 0;
	const filteredNetworkUnits = filteredNetworkList?.units;

	const filteredListIsEmpty =
		filteredNetworkUnits?.unitsByLocality?.length === 0 &&
		filteredNetworkUnits?.unitsByDistance?.length === 0 &&
		filteredNetworkUnits?.unitsSupportAccredited?.length === 0;

	const hasAnyUnit = Boolean(
		unitsByLocality?.length ||
		unitsByDistance?.length ||
		unitResponseState?.units?.unitsSupportAccredited?.length,
	);

	const showNotFoundState = isUnitsNetworksEnabled
		? Boolean(query) && filteredListIsEmpty && hasAnyUnit
		: (notFoundUnits && notFoundAccreditedUnits) || filteredListIsEmpty;

	const hasOnlyTelemedicine = hasOnlyTelemedicineUnit(unitResponseState);

	const showSpecialtyNotAvailableInSelfNetwork = Boolean(
		hasOnlyTelemedicine && unitsByLocality[0].isSpecialtyBlocked,
	);

	const showEmptyAppointmentsCard =
		!query &&
		!showSpecialtyNotAvailableInSelfNetwork &&
		(isGndiSaoPaulo || showAccreditedNetworkToHap
			? hasOnlyTelemedicine && notFoundAccreditedUnits
			: hasOnlyTelemedicine);

	const {showSelfNetwork, showAccreditedNetwork} = getNetworkVisibility({
		isGndiSaoPaulo,
		showEmptyAppointmentsCard,
		isSelfNetwork,
		notFoundUnits,
		notFoundAccreditedUnits,
		localityDistance,
		hasUnitsUpToMaxDistance: hasUnitsUpToMaxDistanceState,
		showAccreditedNetworkToHap,
		isFavoriteDoctorsFlow: bookingData?.isFavoriteDoctorsFlow,
	});

	const telemedicineUnit = filteredNetworkList?.units?.unitsByLocality?.find(
		item => item?.telemedicine === TelemedicineEnum.IS_TELEMEDICINE,
	);

	const showTelehealthSection = Boolean(telemedicineUnit);

	const handleFetchUnits = async () => {
		if (!hasUnitsUpToMaxDistanceState) return;

		setOperationState(OperationStateTypesEnum.SUBMITTING);
		setSpecialtyNotAvailableOnlineState(false);
		setNotFoundErrorState(false);

		abortControllerRef.current = new AbortController();

		const distanceFilterAtMaxDistance = Number(localityDistance) >= MAX_DISTANCE_FILTER_UNITS;

		try {
			resetHealthRequestScheduleState();

			if (!bookingData.isFavoriteDoctorsFlow) {
				const accreditedUnits = await getHealthProviderAccredited({
					signal: abortControllerRef.current.signal,
				});

				setUnitResponseState(prev => ({
					...prev,
					units: {
						...prev.units,
						unitsSupportAccredited: accreditedUnits,
					},
				}));
			}

			if (!localityDistance && isUsingGeolocation) {
				setDefaultDistanceUrl();
			}

			const {
				unidadesPorLocal,
				unidadesPorRaio,
				name: unitType,
			} = await getHealthUnits({signal: abortControllerRef.current.signal});

			const isSelfNetwork = unitType === NetworkTypesEnum.SELF;
			const isLocalityListEmpty = unidadesPorLocal.length === 0;
			const isDistanceListEmpty = unidadesPorRaio.length === 0;
			const hasOnlyTelemedicineInLocality =
				unidadesPorLocal.length === 1 &&
				unidadesPorLocal[0]?.telemedicine === TelemedicineEnum.IS_TELEMEDICINE;

			const isEmptyUnits = (isLocalityListEmpty && isDistanceListEmpty) || !isSelfNetwork;

			const shoulCheckUnitsUpToMaxDistance = !isPreviewUnitsFetchedState && isEmptyUnits;

			if (shoulCheckUnitsUpToMaxDistance) {
				const hasUnitsUpToMaxDistance = await getHealthProviderPreview({
					signal: abortControllerRef.current.signal,
				});
				setIsPreviewUnitsFetchedState(true);
				setHasUnitsUpToMaxDistanceState(hasUnitsUpToMaxDistance.Content);

				if (hasUnitsUpToMaxDistance.Content === false) {
					if (pacmanFlagIsEnabled) {
						await handleGetHealthRequestSchedulePermission(
							requestSchedulePermissionParams,
						);
					}

					setOperationState(OperationStateTypesEnum.DONE);
					return;
				}
			}

			const shouldRefetchUnits =
				isManualLocation &&
				!localityDistance &&
				(!isSelfNetwork || hasOnlyTelemedicineInLocality || isLocalityListEmpty);

			if (shouldRefetchUnits) {
				setRefetchSearchParams();

				return;
			}

			const hasNoSelfNetworkUnits =
				isEmptyUnits || (hasOnlyTelemedicineInLocality && isDistanceListEmpty);

			const shouldExpandToMaxDistance =
				isUnitsNetworksEnabled &&
				!isManualDistanceSearch &&
				!distanceFilterAtMaxDistance &&
				hasNoSelfNetworkUnits;

			if (shouldExpandToMaxDistance) {
				setMaxDistanceSearchParams();

				return;
			}

			const shouldRequestHealthSchedulePermission =
				pacmanFlagIsEnabled && distanceFilterAtMaxDistance && isEmptyUnits;

			if (shouldRequestHealthSchedulePermission) {
				await handleGetHealthRequestSchedulePermission(requestSchedulePermissionParams);
			}

			setUnitResponseState(prev => ({
				networkType: {
					code: '',
					name:
						unitType === NetworkTypesEnum.SELF
							? NetworkTypesEnum.SELF
							: NetworkTypesEnum.SUPPORT,
				},
				units: {
					...prev.units,
					unitsByLocality: unidadesPorLocal,
					unitsByDistance: unidadesPorRaio,
				},
			}));

			setOperationState(OperationStateTypesEnum.DONE);
		} catch (error) {
			if (isUnprocessableError(error)) {
				setNotFoundErrorState(true);

				if (isNotAllowedForSpecialtyError(error)) {
					setSpecialtyNotAvailableOnlineState(true);
					setHideSearchBarState(true);

					setOperationState(OperationStateTypesEnum.DONE);
					return;
				}

				const shoulCheckHealthUnitsUpToMaxDistance = !isPreviewUnitsFetchedState;

				if (shoulCheckHealthUnitsUpToMaxDistance) {
					const hasUnitsUpToMaxDistance = await getHealthProviderPreview({
						signal: abortControllerRef.current.signal,
					});
					setIsPreviewUnitsFetchedState(true);
					setHasUnitsUpToMaxDistanceState(hasUnitsUpToMaxDistance.Content);

					if (hasUnitsUpToMaxDistance.Content === false) {
						if (pacmanFlagIsEnabled) {
							await handleGetHealthRequestSchedulePermission(
								requestSchedulePermissionParams,
							);
						}

						setOperationState(OperationStateTypesEnum.DONE);

						return;
					}
				}

				if (pacmanFlagIsEnabled && distanceFilterAtMaxDistance) {
					await handleGetHealthRequestSchedulePermission(requestSchedulePermissionParams);
				}

				if (isUsingGeolocation) {
					setUnitResponseState(prev => ({
						networkType: {
							code: '',
							name: NetworkTypesEnum.SELF,
						},
						units: {
							...prev.units,
							unitsByLocality: [],
							unitsByDistance: [],
						},
					}));
				}

				setOperationState(OperationStateTypesEnum.DONE);

				return;
			}

			setOperationState(OperationStateTypesEnum.ERROR);
		}
	};

	const setDefaultDistanceUrl = () => {
		const params = new URLSearchParams(window.location.search);
		params.delete(QueryParamsEnum.DISTANCE_ORIGIN);
		params.set(QueryParamsEnum.LOCALITY_DISTANCE, MIN_DISTANCE_IN_KM_HEALTH);
		setSearchParams(params);
	};

	const setDistanceSearchParams = (distance: string) => {
		const params = new URLSearchParams(window.location.search);
		params.set(QueryParamsEnum.LOCALITY_DISTANCE, distance);
		params.delete(QueryParamsEnum.DISTANCE_ORIGIN);
		params.delete(QueryParamsEnum.SEARCH_QUERY);
		setSearchParams(params);
	};

	const setRefetchSearchParams = () => {
		setDistanceSearchParams(KM_TO_REFETCH);
	};

	const setMaxDistanceSearchParams = () => {
		setDistanceSearchParams(MAX_DISTANCE_FILTER_UNITS.toString());
	};

	useEffect(() => {
		handleFetchUnits();
	}, [effectiveLocalityDistance]);

	useEffect(
		() => () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
				abortControllerRef.current = null;
			}
		},
		[],
	);

	return {
		specialtyNotAvailableOnline: specialtyNotAvailableOnlineState,
		showLoadingState,
		handleRefetchUnits: handleFetchUnits,
		showErrorState,
		allowsRequestScheduleRegistration,
		healthRequestSchedulePermissionState,
		showNotFoundState,
		query,
		showSelfNetwork,
		showAccreditedNetwork,
		showEmptyAppointmentsCard,
		showSpecialtyNotAvailableInSelfNetwork,
		shouldReverseNetworkOrder,
		showTelehealthSection,
		telemedicineUnit,
		unitsList,
	};
}
